import axios from "axios";
import { getTenantSubdomain } from "@/config/brand";
import {
  getAuthToken,
  hasWebAuthSession,
  WEB_AUTH_SESSION_EVENT_STORAGE_KEY,
} from "@/lib/auth";
import { storage } from "@/utils/storage";

const DEFAULT_RATE_LIMIT_COOLDOWN_MS = 30_000;

const inFlightRequests = new Map<string, Promise<unknown>>();
const rateLimitedUntilByKey = new Map<string, number>();
const MAX_COOLDOWN_KEYS = 1000;
let activeScope: string | undefined;

// The same key may be requested after login, logout, impersonation or a tenant
// change. Keep credentials out of request keys/error messages, and only retain
// the current scope's registry. Cookie-session changes have their own nonce.
export function getSharedApiRequestScope(): string {
  let sessionChange: string | null = null;
  try {
    sessionChange = storage.getItem(WEB_AUTH_SESSION_EVENT_STORAGE_KEY);
  } catch {
    // Browsers that deny storage still get the existing anonymous behavior.
  }
  return JSON.stringify([
    getAuthToken(),
    hasWebAuthSession(),
    sessionChange,
    getTenantSubdomain(),
  ]);
}

export class StaleRequestScopeError extends Error {
  constructor() {
    super("The request belongs to a previous authentication session");
    this.name = "StaleRequestScopeError";
  }
}

export class LocalRateLimitError extends Error {
  retryAfterMs: number;

  constructor(key: string, retryAfterMs: number) {
    super(`Request is rate limited locally: ${key}`);
    this.name = "LocalRateLimitError";
    this.retryAfterMs = retryAfterMs;
  }
}

export function isRateLimitError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 429;
}

export function isLocalRateLimitError(
  error: unknown
): error is LocalRateLimitError {
  return error instanceof LocalRateLimitError;
}

function readRetryAfterHeader(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const headers = error.response?.headers;
  if (!headers) return undefined;

  if (typeof headers.get === "function") {
    const value = headers.get("retry-after");
    return value == null ? undefined : String(value);
  }

  const record = headers as Record<string, unknown>;
  const value = record["retry-after"] ?? record["Retry-After"];
  return value == null ? undefined : String(value);
}

export function getRateLimitCooldownMs(
  error: unknown,
  fallbackMs = DEFAULT_RATE_LIMIT_COOLDOWN_MS
): number {
  const retryAfter = readRetryAfterHeader(error);
  if (!retryAfter) return fallbackMs;

  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.max(1000, seconds * 1000);
  }

  const dateMs = Date.parse(retryAfter);
  if (Number.isFinite(dateMs)) {
    return Math.max(1000, dateMs - Date.now());
  }

  return fallbackMs;
}

export async function runSharedApiRequest<T>(
  key: string,
  request: () => Promise<T>,
  options: { rateLimitCooldownMs?: number } = {}
): Promise<T> {
  const scope = getSharedApiRequestScope();
  if (scope !== activeScope) {
    activeScope = scope;
    inFlightRequests.clear();
    rateLimitedUntilByKey.clear();
  }
  const now = Date.now();
  for (const [limitedKey, until] of rateLimitedUntilByKey) {
    if (until <= now) rateLimitedUntilByKey.delete(limitedKey);
  }
  const rateLimitedUntil = rateLimitedUntilByKey.get(key) ?? 0;
  if (rateLimitedUntil > now) {
    throw new LocalRateLimitError(key, rateLimitedUntil - now);
  }

  const existing = inFlightRequests.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const promise = request()
    .then((value) => {
      if (getSharedApiRequestScope() !== scope)
        throw new StaleRequestScopeError();
      return value;
    })
    .catch((error) => {
      if (getSharedApiRequestScope() !== scope)
        throw new StaleRequestScopeError();
      if (isRateLimitError(error) && activeScope === scope) {
        if (
          !rateLimitedUntilByKey.has(key) &&
          rateLimitedUntilByKey.size >= MAX_COOLDOWN_KEYS
        ) {
          const oldestKey = rateLimitedUntilByKey.keys().next().value;
          if (oldestKey !== undefined) rateLimitedUntilByKey.delete(oldestKey);
        }
        rateLimitedUntilByKey.set(
          key,
          Date.now() +
            getRateLimitCooldownMs(error, options.rateLimitCooldownMs)
        );
      }
      throw error;
    })
    .finally(() => {
      // An older session's request may finish after a newer one has reused its
      // key. It must not remove that active promise or cancel its consumers.
      if (inFlightRequests.get(key) === promise) inFlightRequests.delete(key);
    });

  inFlightRequests.set(key, promise);
  return promise;
}

export function clearSharedApiRequestStateForTests() {
  activeScope = undefined;
  inFlightRequests.clear();
  rateLimitedUntilByKey.clear();
}
